import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Oauth2Service } from '@api/services';
import { TokenService } from '@api/services/authorization/token.service';
import { UserImageService } from '@api/services/user/user-image.service';
import { BreadcrumbService, LoginUser, LoginUserService } from '@smartera/core';
import { UtilityService } from '@core/services/utility.service';
import { BaseComponent } from '@modules/components/base-component';
import { SideMenu } from '@modules/security/profile/models/menu';
import { MenuService } from '@modules/security/profile/services/menu.service';
import { MenuItem } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { fade, fadeAndSlideUp } from '@smartera/animations';
import { ThemeService } from '@core/services';
import { User } from '@api/models';

@Component({
	selector: 'app-header-menu',
	templateUrl: './header-menu.component.html',
	styleUrls: ['./header-menu.component.scss'],
	animations: [fadeAndSlideUp, fade],
	standalone: false,
})
export class HeaderMenuComponent extends BaseComponent implements OnInit {
	menuItems: MenuItem[] = [];
	items: MenuItem[] = [];
	breadCrumb: MenuItem[] = [];
	sidebarVisible: boolean = false;
	isShowInteractiveDemoDisplayHomepage: boolean = false;
	isShowInteractiveDemoDisplayReportPreparation: boolean = false;
	isShowInteractiveDemoDisplayWidget: boolean = false;

	@Input() public menuHidden = false;
	public _userImageUrl = './assets/img/blank-user.png';

	constructor(
		utilServ: UtilityService,
		private _loginUserService: LoginUserService,
		public _userImage: UserImageService,
		private tokenService: TokenService,
		private _oauth2Service: Oauth2Service,
		private _router: Router,
		private breadCrumbServ: BreadcrumbService,
		private themeService: ThemeService,
		private menuService: MenuService
	) {
		super(null, utilServ);
		breadCrumbServ.modalChange.subscribe((modal) => {
			this.breadCrumb = modal;
		});
	}

	async ngOnInit(): Promise<void> {
		if (environment.demo) {
			this.menuItems = [
				{
					label: this.t('g.b.dashboard'),
					icon: 'fas fa-home',
					routerLink: ['/page/dashboard'],
				},
				{
					label: this.t('report-designer'),
					icon: 'fas fa-file-invoice',
					routerLink: ['/page/smart-report/designer-reports'],
				},
				{
					label: this.t('report-view'),
					icon: 'fas fa-file-contract',
					routerLink: ['/page/smart-report/result-reports'],
				},
				{
					label: this.t('admin-panel'),
					icon: 'fas fa-user-gear',
					routerLink: ['/page/smart-report/admin-panel'],
				},
			];
		} else {
			const menuList = (await lastValueFrom(this.menuService.getSideBarMenuByAppId()))?.children[0]?.children ?? [];
			this.menuItems = menuList.map((menu) => this.prepareMenu(menu));
		}

		this.translationInit(() => {
			this.items = [
				{
					label: this.t('top-bar.profile'),
					icon: 'fas fa-user',
					routerLink: ['/page/security/personal'],
				},
				{
					label: this.t('top-bar.logout'),
					icon: 'fas fa-power-off',
					command: () => {
						this.tokenService.close();
						this._router.navigateByUrl('/login');
						this._oauth2Service.logout();
					},
				},
			];
		});
	}

	private prepareMenu(menu: SideMenu): MenuItem {
		if (menu.children.length > 0) {
			return {
				label: this.t(menu.label),
				routerLink: menu.url,
				icon: this.prepareMenuIcon(menu),
				items: menu.children.map((subMenu) => this.prepareMenu(subMenu)),
			};
		} else if (menu.label == 'page.smartyCubes.report-create.parent') {
			return {
				label: this.t(menu.label),
				routerLink: [menu.url],
				styleClass: 'reportCreateButton',
				icon: this.prepareMenuIcon(menu),
			};
		} else {
			return {
				label: this.t(menu.label),
				routerLink: [menu.url],
				icon: this.prepareMenuIcon(menu),
			};
		}
	}

	private prepareMenuIcon(menu: SideMenu): string {
		switch (menu.label) {
			case 'page.smartyCubes.dashboard.parent':
				return 'fas fa-home';
			case 'page.smartyCubes.security.parent':
				return 'fas fa-shield-alt';
			case 'page.smartyCubes.reports.parent':
				return 'fas fa-chart-line';
			case 'page.smart-report.admin-panel':
				return 'fas fa-chart-line';
			case 'page.smart-report.admin-manager':
				return 'fas fa-user-gear';
			case 'page.smartyCubes.data-collector.parent':
				return 'fas fa-chart-line';
			case 'page.smartyCubes.settings.parent':
				return 'fas fa-user-gear';
		}
	}

	get user(): LoginUser {
		return this._loginUserService.user;
	}

	get userImageUrl() {
		return this._userImage.image;
	}

	public showIframeAgainDisplayHomepage(): void {
		this.sidebarVisible = false;
		this.isShowInteractiveDemoDisplayHomepage = !this.isShowInteractiveDemoDisplayHomepage;
	}

	public showIframeAgainReportPreparation(): void {
		this.sidebarVisible = false;
		this.isShowInteractiveDemoDisplayReportPreparation = !this.isShowInteractiveDemoDisplayReportPreparation;
	}

	public showIframeAgainWidget(): void {
		this.sidebarVisible = false;
		this.isShowInteractiveDemoDisplayWidget = !this.isShowInteractiveDemoDisplayWidget;
	}

	showHomePage(): void {
		if (this.menuItems.length > 0) {
			this._router.navigateByUrl('/page/dashboard');
		}
	}
}
